import { Link } from '@tanstack/react-router'
import type { PostureTopEntry } from '@bloo-team/api-client'

/**
 * Top-N list panel with count bars on a shared max scale (the home /
 * coverage `.topn` pattern). Wrapped in a Link when the list has a landing
 * page; a plain panel otherwise. Overview shows up to 8 rows; the Signals
 * stats detail row uses the bare TopList (top 5, one line per row).
 */
export function TopPanel({
  label,
  to,
  rows,
  emptyText,
  span = 'hud-span-3',
  loading = false,
  failed = false,
  limit = 8,
}: {
  label: string
  to?: string
  rows: PostureTopEntry[]
  emptyText: string
  span?: string
  loading?: boolean
  /** The read behind the rows failed — says so instead of the empty text. */
  failed?: boolean
  /** Rows shown, from the top. */
  limit?: number
}) {
  const body = (
    <div className="hud-stack">
      <span className="hud-eyebrow">{label}</span>
      <TopList rows={rows} limit={limit} loading={loading} failed={failed} emptyText={emptyText} />
    </div>
  )
  return to ? (
    <Link to={to} className={`hud-panel ${span}`}>
      {body}
    </Link>
  ) : (
    <div className={`hud-panel ${span}`}>{body}</div>
  )
}

/** The rows alone. `compact` puts each row on one line — name, bar, count. */
export function TopList({
  rows,
  emptyText,
  loading = false,
  failed = false,
  limit = 8,
  compact = false,
  label,
}: {
  rows: PostureTopEntry[]
  emptyText: string
  loading?: boolean
  failed?: boolean
  limit?: number
  compact?: boolean
  /** Accessible name for the table, when no visible heading names it. */
  label?: string
}) {
  const shown = rows.slice(0, limit)
  const max = shown.reduce((m, r) => Math.max(m, r.count), 1)
  if (shown.length === 0) {
    return (
      <p className="hud-unit" role="status">
        {loading ? 'Loading…' : failed ? 'Statistics unavailable.' : emptyText}
      </p>
    )
  }
  return (
    <table className={compact ? 'topn topn-compact' : 'topn'} aria-label={label}>
      <tbody>
        {shown.map((r) => {
          const bar = <span className="bar" style={{ width: `${(r.count / max) * 100}%` }} />
          return (
            <tr key={r.name}>
              <td className="lbl" title={r.name}>
                {r.name}
                {compact ? null : bar}
              </td>
              {compact ? (
                <td className="barcell" aria-hidden="true">
                  {bar}
                </td>
              ) : null}
              <td className="n">{r.count.toLocaleString('en-US')}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
